import { landmineNames } from '@mm/constants/landmine-names';
import { DragIcon, TrashIcon } from '@mm/icons';
import { useLeadAssignmentsCRUDActions } from '@mm/state/actions/LeadAssignmentsCRUDActions';
import { LeadAssignmentGroup } from '@mm/state/interfaces/leadAssignmentGroups';
import { LeadAssignment } from '@mm/state/interfaces/leadAssignments';
import CompanyUsersModel from '@mm/state/model/CompanyUsersModel';
import { useModalActions } from '@mm/state/modules/modalsState/actions';
import { combinedFeatureFlagsSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import alertAction from 'assets/icons/alert-action.svg';
import pencilAltIcon from 'assets/icons/pencil-alt.svg';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import { useSelector } from 'react-redux';
import { UncontrolledTooltip } from 'reactstrap';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import { CollapsibleContentContainerWithoutLocalState } from '../../../../../../../common/components/CollapsibleContentContainer/CollapsibleContentContainerWithoutLocalState';
import RadioInput from '../../../../../../../common/components/RadioInput';
import SelectorWithArrow from '../../../../../../../common/components/SelectorWithArrow/SelectorWithArrow';
import Switch from '../../../../../../../common/components/Switch/Switch';
import { colors } from '../../../../styles/colors';
import { SelectInputWithPopup } from '../SelectInputWithPopup/SelectInputWithPopup';
import './LeadAssignmentItem.pcss';

type Props = {
  record: LeadAssignment;
  agents: CompanyUsersModel[];
  groups: LeadAssignmentGroup[];
  draggableProvided?: DraggableProvided;
};

export const LeadAssignmentItem = (props: Props) => {
  const { record, agents, groups, draggableProvided } = props;
  const activeChatbotId = useSelector(activeChatbotIdSelector);
  const modalActions = useModalActions();
  const canAssignLeads = useSelector(
    combinedFeatureFlagsSelector,
  ).leadsAssignment;
  const [active, setActive] = useState(true);
  const [isAnyContact, setIsAnyContact] = useState(
    record.type === 'any_contact',
  );
  const isAssignToAgentInitialValue =
    !record.agentId && !record.automationLeadAssignmentGroupId
      ? true
      : !!record.agentId;
  const [isAssignToAgent, setIsAssignToAgent] = useState(
    isAssignToAgentInitialValue,
  );
  const [assignToId, setAssignToId] = useState(
    record.agentId || record.automationLeadAssignmentGroupId,
  );
  const [newNameValue, setNewNameValue] = useState(record.name || '');
  const [editName, setEditName] = useState(false);
  const [matchesFieldsRequired, setMatchesFieldsRequired] = useState(false);
  const [assignFieldRequired, setAssignFieldRequired] = useState(false);

  const { update, delete: deleteItem } = useLeadAssignmentsCRUDActions();
  const { openGeneralConfirmModal } = useModalActions();

  const assignToItems = isAssignToAgent
    ? agents.map(agent => {
        return {
          id: agent.id,
          name: `${agent.firstName} ${agent.lastName}`,
        };
      })
    : groups.map(group => {
        return {
          id: group.id,
          name: `${group.name}`,
        };
      });

  const getSelectedOption = () => {
    if (assignToId) {
      return assignToItems.find(o => o.id === assignToId);
    }
  };

  const requiredFieldsEmpty =
    !isAnyContact &&
    !record.tags.length &&
    !record.audienceIds?.length &&
    !record.variableFilters?.length;

  useEffect(() => {
    if (!assignToId) {
      setAssignFieldRequired(true);
    } else {
      setAssignFieldRequired(false);
    }
    if (requiredFieldsEmpty) {
      setMatchesFieldsRequired(true);
    } else {
      setMatchesFieldsRequired(false);
    }
  }, [requiredFieldsEmpty, assignToId]);

  const onToggleActivationLabel = () => {
    if (!assignToId || requiredFieldsEmpty) {
      openGeneralConfirmModal({
        showHeader: false,
        headerText: '',
        messageText: `Required fields are missing`,
        showCancelButton: false,
        onCloseHandler: () => {},
      });
      return;
    }
    if (!canAssignLeads) {
      modalActions.openUniversalUpgradeLandmineModal({
        landmineName: landmineNames['LEAD-ASSIGNMENT__ACTIVATION-CHECKBOX'],
        feature: 'leadsAssignment',
      });
    } else {
      update(activeChatbotId, record.id, {
        active: !record.active,
      });
    }
  };

  const updateLeadAssignmentType = (anyContact: boolean) => {
    setIsAnyContact(anyContact);
    const updatedFields: Partial<LeadAssignment> = anyContact
      ? {
          type: 'any_contact',
          tags: [],
          audienceIds: [],
          variableFilters: [],
        }
      : {
          type: 'matches_all',
        };

    update(activeChatbotId, record.id, updatedFields);
  };

  const updateAssignTo = (id: number) => {
    if (id) {
      setAssignToId(id);
      update(activeChatbotId, record.id, {
        agentId: isAssignToAgent ? id : null,
        automationLeadAssignmentGroupId: !isAssignToAgent ? id : null,
      });
    }
  };

  const onDeleteItem = () => {
    deleteItem(activeChatbotId, record.id);
  };

  const leadAssignmentNameInputComponent = editName ? (
    <p className="lead-assignment-item__header-name-item">
      <input
        className="lead-assignment-item__input-container"
        value={newNameValue}
        placeholder="Value"
        onChange={event => setNewNameValue(event.target.value)}
      />
      <PrimaryButton
        color={ButtonColor.blue}
        className="lead-assignment-item__input-btn"
        disabled={newNameValue.trim().length === 0}
        onClick={() => {
          update(activeChatbotId, record.id, {
            name: newNameValue,
          });
          setEditName(false);
        }}
      >
        Edit
      </PrimaryButton>
      <PrimaryButton
        color={ButtonColor.invertedRed}
        className="lead-assignment-item__input-btn"
        onClick={() => {
          setNewNameValue(record.name || '');
          setEditName(false);
        }}
      >
        Cancel
      </PrimaryButton>
    </p>
  ) : (
    <p className="lead-assignment-item__header-name-item">
      {newNameValue}
      <img
        src={pencilAltIcon}
        className="lead-assignment-item__edit-icon"
        alt=""
        onClick={() => setEditName(true)}
      />
    </p>
  );

  const draggableProps =
    draggableProvided && draggableProvided.draggableProps
      ? draggableProvided.draggableProps
      : {};
  const dragHandleProps =
    draggableProvided && draggableProvided.dragHandleProps
      ? draggableProvided.dragHandleProps
      : {};

  const renderContainerMenu = () => (
    <div
      className="lead-assignment-item__menu"
      onClick={e => active && e.stopPropagation()}
    >
      {leadAssignmentNameInputComponent}

      <div className="lead-assignment-item__menu-right">
        <Switch
          checked={record.active}
          disabled={false}
          onChange={onToggleActivationLabel}
        />
        <div className="lead-assignment-item__delete-icon">
          <TrashIcon onClick={onDeleteItem} />
        </div>
        <div className="lead-assignment-item__drag-handle" {...dragHandleProps}>
          <DragIcon color={colors.accentBlue} />
        </div>
      </div>
    </div>
  );

  const leadAssignmentItemClassname = classNames(
    'lead-assignment-item__container',
    {
      'lead-assignment-item__container--error-state': !record.valid,
    },
  );

  return (
    <div
      ref={draggableProvided && draggableProvided.innerRef}
      {...draggableProps}
      {...dragHandleProps}
    >
      <CollapsibleContentContainerWithoutLocalState
        expandedOnMount={true}
        disableExpand={false}
        containerName={renderContainerMenu()}
        className={leadAssignmentItemClassname}
        useCrossIconStyle={true}
        isExpanded={active}
        toggleIsExpanded={value => setActive(value)}
      >
        <div className="lead-assignment-item__content">
          <div className="lead-assignment-item__when-contact-selector-container">
            <div className="lead-assignment-item__radio-input-label">
              When Contact:
            </div>
            <div className="lead-assignment-item__radio-inputs-container">
              <RadioInput
                className="notification-type__input"
                name={`when-contact-${record.id}`}
                checked={isAnyContact}
                value="is-any-contact"
                readOnly
                label="is any contact"
                onClick={() => updateLeadAssignmentType(true)}
              />
              <RadioInput
                className="notification-type__input"
                name={`when-contact-${record.id}`}
                checked={!isAnyContact}
                value="matches-all"
                readOnly
                label="matches ALL of the following"
                onClick={() => updateLeadAssignmentType(false)}
              />
            </div>
            {!isAnyContact && (
              <SelectInputWithPopup
                record={record}
                isExpanded={active}
                fieldsRequired={matchesFieldsRequired}
              />
            )}
          </div>
          <div className="lead-assignment-item__assign-to-selector-container">
            <div className="lead-assignment-item__radio-input-label">
              Assign to:
            </div>
            <div className="lead-assignment-item__radio-inputs-container">
              <RadioInput
                className="lead-assignment-item__radio-input"
                name={`assign-to-${record.id}`}
                checked={isAssignToAgent}
                value="agent"
                readOnly
                label="Agent"
                onClick={() => {
                  setAssignToId(record.agentId);
                  setIsAssignToAgent(true);
                }}
              />
              <RadioInput
                className="lead-assignment-item__radio-input"
                name={`assign-to-${record.id}`}
                checked={!isAssignToAgent}
                value="group"
                readOnly
                label="Group"
                onClick={() => {
                  setAssignToId(record.automationLeadAssignmentGroupId);
                  setIsAssignToAgent(false);
                }}
              />
            </div>
            <div className="lead-assignment-item__agent-selector-container">
              <SelectorWithArrow
                selectedItem={getSelectedOption()}
                onChange={id => updateAssignTo(Number(id))}
                items={assignToItems}
                defaultLabel={isAssignToAgent ? 'Select Agent' : 'Select Group'}
                className="lead-assignment-item__agent-selector"
              />
              {assignFieldRequired && (
                <>
                  <img
                    className="select-input__error-icon"
                    id="assignFieldRequired"
                    src={alertAction}
                  />
                  <UncontrolledTooltip
                    className="widget-component__tooltip"
                    innerClassName="widget-component__inner-tooltip"
                    target="assignFieldRequired"
                  >
                    {'Required fields are missing'}
                  </UncontrolledTooltip>
                </>
              )}
            </div>
            <div className="lead-assignment-item__info-message">
              Add agent groups below
            </div>
          </div>
        </div>
      </CollapsibleContentContainerWithoutLocalState>
    </div>
  );
};
